import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAdminUsers, useCreateUser, usePatchUser } from '@/hooks/useAdmin'
import { ApiError } from '@/lib/api/rawFetch'
import type { AdminUserResponse } from '@/lib/api/session11Types'

const ROLES = ['admin', 'approver', 'buyer'] as const

export function UsersSection() {
  const { data: users = [], isLoading } = useAdminUsers()
  const createMut = useCreateUser()
  const patchMut = usePatchUser()

  const [newUser, setNewUser] = useState({ username: '', display_name: '', role: 'buyer', email: '', password: '' })
  const [editId, setEditId] = useState<number | null>(null)
  const [editDraft, setEditDraft] = useState<{ display_name?: string; role?: string; email?: string; is_active?: boolean; password?: string }>({})

  async function submitCreate() {
    if (!newUser.username || !newUser.password) {
      toast.error('Username and password required')
      return
    }
    try {
      // Empty email → backend defaults to purchasing@dapperhorse.com via create_user.
      await createMut.mutateAsync(newUser)
      toast.success(`Created user ${newUser.username}`)
      setNewUser({ username: '', display_name: '', role: 'buyer', email: '', password: '' })
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toast.error('Username already exists')
      } else {
        const msg = err instanceof Error ? err.message : 'create failed'
        toast.error(msg)
      }
    }
  }

  function startEdit(u: AdminUserResponse) {
    setEditId(u.user_id)
    setEditDraft({ display_name: u.display_name ?? '', role: u.role, email: u.email ?? '', is_active: u.is_active })
  }

  async function saveEdit() {
    if (editId == null) return
    try {
      const body: Record<string, unknown> = {}
      if (editDraft.display_name !== undefined) body.display_name = editDraft.display_name
      if (editDraft.role !== undefined) body.role = editDraft.role
      if (editDraft.email !== undefined) body.email = editDraft.email
      if (editDraft.is_active !== undefined) body.is_active = editDraft.is_active
      if (editDraft.password) body.password = editDraft.password
      await patchMut.mutateAsync({ userId: editId, body: body as Parameters<typeof patchMut.mutateAsync>[0]['body'] })
      toast.success('User updated')
      setEditId(null)
      setEditDraft({})
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'update failed'
      toast.error(msg)
    }
  }

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">User Management</h2>

      {isLoading ? (
        <div className="space-y-1">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-7 w-full" />)}</div>
      ) : (
        <div className="overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)]">
          <table className="text-xs w-full">
            <thead className="bg-[var(--surface-header)] border-b border-[var(--line-warm)]">
              <tr className="text-left text-[var(--mute)]">
                <th className="tbl-th">Username</th>
                <th className="tbl-th">Display Name</th>
                <th className="tbl-th">Role</th>
                <th className="tbl-th">Email</th>
                <th className="tbl-th">Last Login</th>
                <th className="tbl-th">Active</th>
                <th className="tbl-th" />
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const editing = editId === u.user_id
                return (
                  <tr
                    key={u.user_id}
                    className={`border-b border-[var(--line-warm)] ${
                      i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'
                    }`}
                  >
                    <td className="px-2 py-1 font-medium">{u.username}</td>
                    <td className="px-2 py-1">
                      {editing ? (
                        <input value={editDraft.display_name ?? ''} onChange={(e) => setEditDraft({ ...editDraft, display_name: e.target.value })} className="w-full px-1 py-0.5 border border-[var(--line-input)] bg-transparent focus:outline-none focus:border-[var(--brand)]" />
                      ) : (u.display_name ?? '—')}
                    </td>
                    <td className="px-2 py-1">
                      {editing ? (
                        <select value={editDraft.role} onChange={(e) => setEditDraft({ ...editDraft, role: e.target.value })} className="px-1 py-0.5 border border-[var(--line-input)] bg-transparent focus:outline-none focus:border-[var(--brand)]">
                          {ROLES.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                        </select>
                      ) : (u.role ? u.role.charAt(0).toUpperCase() + u.role.slice(1) : '—')}
                    </td>
                    <td className="px-2 py-1">
                      {editing ? (
                        <input
                          type="email"
                          value={editDraft.email ?? ''}
                          onChange={(e) => setEditDraft({ ...editDraft, email: e.target.value })}
                          placeholder="user@dapperhorse.com"
                          className="w-full px-1 py-0.5 border border-[var(--line-input)] bg-transparent focus:outline-none focus:border-[var(--brand)]"
                        />
                      ) : (u.email ?? '—')}
                    </td>
                    <td className="px-2 py-1 text-[var(--ink-2)]">{u.last_login?.slice(0, 16).replace('T', ' ') ?? '—'}</td>
                    <td className="px-2 py-1">
                      {editing ? (
                        <input type="checkbox" checked={editDraft.is_active ?? true} onChange={(e) => setEditDraft({ ...editDraft, is_active: e.target.checked })} />
                      ) : u.is_active ? 'yes' : 'no'}
                    </td>
                    <td className="px-2 py-1 text-right">
                      {editing ? (
                        <div className="flex gap-1 justify-end">
                          <input type="password" placeholder="new password (optional)" value={editDraft.password ?? ''} onChange={(e) => setEditDraft({ ...editDraft, password: e.target.value })} className="px-1 py-0.5 border border-[var(--line-input)] text-xs bg-transparent focus:outline-none focus:border-[var(--brand)]" />
                          <Button size="sm" className="h-6 text-xs" onClick={saveEdit} disabled={patchMut.isPending}>Save</Button>
                          <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => { setEditId(null); setEditDraft({}) }}>Cancel</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" className="h-6 text-xs" onClick={() => startEdit(u)}>Edit</Button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="border border-[var(--line-warm)] bg-[var(--surface-card)] p-3 space-y-2">
        <h3 className="text-xs font-semibold">Add User</h3>
        <div className="flex flex-wrap gap-2 items-end">
          <label className="text-xs flex flex-col">
            Username
            <input value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} className="px-2 py-1 border border-[var(--line-input)] bg-transparent focus:outline-none focus:border-[var(--brand)]" />
          </label>
          <label className="text-xs flex flex-col">
            Display Name
            <input value={newUser.display_name} onChange={(e) => setNewUser({ ...newUser, display_name: e.target.value })} className="px-2 py-1 border border-[var(--line-input)] bg-transparent focus:outline-none focus:border-[var(--brand)]" />
          </label>
          <label className="text-xs flex flex-col">
            Role
            <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="px-2 py-1 border border-[var(--line-input)] bg-transparent focus:outline-none focus:border-[var(--brand)]">
              {ROLES.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </label>
          <label className="text-xs flex flex-col">
            Email
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              placeholder="purchasing@dapperhorse.com"
              className="px-2 py-1 border border-[var(--line-input)] bg-transparent focus:outline-none focus:border-[var(--brand)]"
            />
          </label>
          <label className="text-xs flex flex-col">
            Password
            <input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="px-2 py-1 border border-[var(--line-input)] bg-transparent focus:outline-none focus:border-[var(--brand)]" />
          </label>
          <Button size="sm" className="h-7 text-xs" onClick={submitCreate} disabled={createMut.isPending}>Create User</Button>
        </div>
        <p className="text-[10px] text-[var(--mute)]">
          Leave email blank for buyers — they’ll default to the shared <code>purchasing@dapperhorse.com</code> inbox.
        </p>
      </div>
    </section>
  )
}
